import { Component, Input, OnInit } from '@angular/core';
import {
    assignModelOptions,
    DisplayDecorator,
    DropdownModel,
    DropdownModelOptions,
    isDefined,
    ModalClass,
    TextModel,
} from '@evorait/pace-core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { SmartlockerAccessCodeComponent } from '../smartlocker-access-code/smartlocker-access-code.component';
import { catchError, map, Observable, of, Subject, take, throwError } from 'rxjs';
import { faArrowDown, faDoorOpen, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { Delivery, SmartlockerMaterial } from '../../interfaces/smartlocker-deliveries.interface';
import { SmartlockerTransferModel } from '../../models/smartlocker-transfer.model';
import { SmartlockerDoorData } from '../../interfaces/smartlocker-doors.interface';
import { SmartlockerService } from '../../services/smartlocker.service';
import {
    SmartlockerMaterialSortingRequest,
    SmartlockerSortingRequest,
} from '../../interfaces/smartlocker-sorting.interface';

enum SMARTLOCKER_TRANSFER_STATE {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Success = 'Success',
    Failed = 'Failed',
}

@Component({
    selector: 'pace-smartlocker-transfer',
    templateUrl: './smartlocker-transfer.component.html',
    styleUrls: ['./smartlocker-transfer.component.scss'],
    standalone: false,
})
export class SmartlockerTransferComponent implements OnInit {
    @Input() public material: SmartlockerMaterial | undefined;

    public model: SmartlockerTransferModel;
    public smartlockerTransferForm: FormGroup;
    public formChildren: FormArray;
    public destinationAccessCode: string;
    public modalResult = new Subject<boolean>();
    public transferState: SMARTLOCKER_TRANSFER_STATE = SMARTLOCKER_TRANSFER_STATE.NotStarted;
    public availableStates = SMARTLOCKER_TRANSFER_STATE;
    protected readonly faArrowDown = faArrowDown;
    protected readonly faMapMarkerAlt = faMapMarkerAlt;
    protected readonly faDoorOpen = faDoorOpen;

    private originLockerId: number;
    private originDoorId: number;
    public destinationDoors: DropdownModel<TextModel>;
    public originDoor: SmartlockerDoorData | undefined;
    private readonly delivery: Delivery;
    public originDoorDetails = '';

    public constructor(
        private readonly formBuilder: FormBuilder,
        private readonly smartlockerService: SmartlockerService,
        private readonly modalInstance: BsModalRef,
        private readonly modalService: BsModalService,
    ) {
        this.delivery = this.smartlockerService.getSelectedDelivery();
        this.model = new SmartlockerTransferModel(this.delivery.AWB);
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.smartlockerTransferForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });

        this.originLockerId = this.material ? this.material.SmartlockerId : this.delivery.SmartlockerId;
        this.originDoorId = this.material ? this.material.SmartlockerDoorId : this.delivery.SmartlockerDoorId;

        this.destinationDoors = new DropdownModel<TextModel>(
            'Destination doors',
            [],
            new DropdownModelOptions({
                required: true,
            }),
        );
        this.smartlockerService
            .getDoorsForLocker(this.originLockerId)
            .pipe(take(1))
            .subscribe((doorsForLocker) => {
                this.originDoor = doorsForLocker.find(
                    (destinationDoor) => destinationDoor.SmartlockerDoorId === this.originDoorId,
                );

                if (this.originDoor) {
                    this.originDoorDetails = this.getDoorDetailsString(this.originDoor);
                }

                this.destinationDoors.value = doorsForLocker
                    .filter((destinationDoor) => destinationDoor.SmartlockerDoorId !== this.originDoorId)
                    .map(
                        (door) =>
                            new DisplayDecorator<TextModel>(
                                this.getDoorDetailsString(door),
                                new TextModel('Destination door', door.SmartlockerDoorId.toString()),
                            ),
                    );
            });
    }

    private getDoorDetailsString(doorData: SmartlockerDoorData): string {
        return `${doorData.DoorIdentifier} - ${doorData.Name} (Door ID: ${doorData.SmartlockerDoorId})`;
    }

    public getDestinationAccessCode(): void {
        // Immediately disable input fields
        this.destinationDoors = assignModelOptions(this.destinationDoors, {
            ...this.destinationDoors,
            disabled: true,
        });
        this.model.awbPackageNumber = assignModelOptions(this.model.awbPackageNumber, {
            ...this.model.awbPackageNumber.options,
            readonly: true,
        });

        const modalInstance = this.modalService.show(SmartlockerAccessCodeComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                smartlockerId: this.originLockerId,
                smartlockerDoorId: parseInt(this.destinationDoors.selectedEntry?.model.value, 10),
            },
            ariaLabelledBy: 'smartlocker-access-code-title',
        });

        modalInstance.content?.modalResult.pipe(take(1)).subscribe((accessCode) => {
            if (accessCode && accessCode.length > 0) {
                this.destinationAccessCode = accessCode;

                // User confirms the destination door is opened so the transfer can
                // be manually completed. Let's first send the request to the server
                // This approach would prevent the user from transferring if the request fails
                this.transfer();
            } else {
                this.destinationDoors = assignModelOptions(this.destinationDoors, {
                    ...this.destinationDoors,
                    disabled: false,
                });
                this.model.awbPackageNumber = assignModelOptions(this.model.awbPackageNumber, {
                    ...this.model.awbPackageNumber.options,
                    readonly: false,
                });
            }
        });
    }

    public transfer(): void {
        this.transferState = SMARTLOCKER_TRANSFER_STATE.InProgress;

        const transfer$ = isDefined(this.material) ? this.transferMaterial() : this.transferAwbPackage();
        transfer$.pipe(take(1)).subscribe((isSuccess) => {
            this.modalResult.next(isSuccess);
            this.transferState = isSuccess ? SMARTLOCKER_TRANSFER_STATE.Success : SMARTLOCKER_TRANSFER_STATE.Failed;
            this.destinationDoors = assignModelOptions(this.destinationDoors, {
                ...this.destinationDoors,
                disabled: isSuccess,
            });
            this.model.awbPackageNumber = assignModelOptions(this.model.awbPackageNumber, {
                ...this.model.awbPackageNumber.options,
                readonly: isSuccess,
            });
        });
    }

    public transferAwbPackage(): Observable<boolean> {
        const destinationDoorId = parseInt(this.destinationDoors.selectedEntry.model.value, 10);
        const request: SmartlockerSortingRequest = {
            Data: [
                {
                    AWB: this.model.awbPackageNumber.value,
                    AccessCode: this.destinationAccessCode,
                    DestinationDoorId: destinationDoorId,
                },
            ],
        };

        return this.smartlockerService.smartlockerSort(request).pipe(
            map((res) => res?.IsSuccess),
            catchError(() => of(false)),
        );
    }

    public transferMaterial(): Observable<boolean> {
        if (!isDefined(this.material)) {
            return throwError(() => `SmartlockerTransferComponent: transferMaterial: material is not defined`);
        }

        const request: SmartlockerMaterialSortingRequest = {
            AWB: this.model.awbPackageNumber.value,
            Data: [
                {
                    ILPN: this.material.ILPN,
                    AccessCode: this.destinationAccessCode,
                    DestinationDoorId: this.destinationDoors.selectedEntry.model.value,
                },
            ],
        };

        return this.smartlockerService.smartlockerMaterialSort(request).pipe(
            map((res) => res?.IsSuccess),
            catchError(() => of(false)),
        );
    }

    public close(): void {
        this.modalResult.complete();
        this.modalInstance.hide();
        if (this.transferState === SMARTLOCKER_TRANSFER_STATE.Success) {
            // User may cancel instead of clicking on Done button
            // If the user cancels after the Transfer then refresh, else just close
            this.smartlockerService.refreshSmartlockerDeliveriesSubject.next();
        }
    }
}
